export type SessionSyncMessage = 'session-expired' | 'signed-out' | 'session-changed'

const CHANNEL_NAME = 'hivecloud:user-auth'
const STORAGE_KEY = 'hivecloud:user-auth-sync'
const listeners = new Set<(message: SessionSyncMessage) => void>()
const origin = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random()}`
let channel: BroadcastChannel | undefined
let initialized = false
let lastSentAt = 0
const received = new Set<string>()

interface SessionSyncEnvelope {
  type: SessionSyncMessage
  origin: string
  at: number
}

function isEnvelope(value: unknown): value is SessionSyncEnvelope {
  if (!value || typeof value !== 'object') return false
  const envelope = value as Partial<SessionSyncEnvelope>
  return (envelope.type === 'session-expired' || envelope.type === 'signed-out' || envelope.type === 'session-changed')
    && typeof envelope.origin === 'string' && envelope.origin.length > 0 && envelope.origin.length <= 128
    && typeof envelope.at === 'number' && Number.isSafeInteger(envelope.at) && envelope.at >= 0
}

function receive(value: unknown) {
  if (!isEnvelope(value) || value.origin === origin) return
  const id = `${value.origin}:${value.at}:${value.type}`
  if (received.has(id)) return
  received.add(id)
  if (received.size > 32) received.delete(received.values().next().value as string)
  listeners.forEach((listener) => listener(value.type))
}

function initialize() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  if ('BroadcastChannel' in window) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.addEventListener('message', onMessage)
    } catch { /* Storage events remain available if channel construction fails. */ }
  }
  window.addEventListener('storage', onStorage)
}

function onMessage(event: MessageEvent) { receive(event.data) }
function onStorage(event: StorageEvent) {
  if (event.key !== STORAGE_KEY || !event.newValue) return
  try { receive(JSON.parse(event.newValue)) } catch { /* Ignore malformed cross-tab state. */ }
}
function dispose() {
  if (!initialized || typeof window === 'undefined') return
  window.removeEventListener('storage', onStorage)
  channel?.removeEventListener('message', onMessage)
  channel?.close()
  channel = undefined
  initialized = false
}

export function onRemoteSessionChange(listener: (message: SessionSyncMessage) => void): () => void {
  initialize()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (!listeners.size) dispose()
  }
}

export function publishSessionChange(type: SessionSyncMessage) {
  if (typeof window === 'undefined') return
  lastSentAt = Math.max(Date.now(), lastSentAt + 1)
  const envelope: SessionSyncEnvelope = { type, origin, at: lastSentAt }
  try {
    if (channel) channel.postMessage(envelope)
    else if ('BroadcastChannel' in window) {
      const transient = new BroadcastChannel(CHANNEL_NAME)
      try { transient.postMessage(envelope) } finally { transient.close() }
    }
  } catch { /* Publishing through storage may still work. */ }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope))
    window.localStorage.removeItem(STORAGE_KEY)
  } catch { /* BroadcastChannel remains authoritative when storage is unavailable */ }
}
